/**
 * Guest Activity Reports Component
 * Manages email report schedules and manual report sending
 */

import React, { useState, useEffect } from 'react';
import { httpsCallable } from '../firebase/guardedWrites';
import { functions, db } from '../firebase/config';
import { collection, query, where, getDocs, Timestamp, documentId } from 'firebase/firestore';
import placeholderAvatar from '../assets/placeholder-avatar.svg';
import '../styles/GuestActivityReports.css';
import { readOnlyProps } from '../utils/viewAs';

const GuestActivityReports = () => {
  const ro = readOnlyProps(); // disable write controls while viewing as someone
  const [schedules, setSchedules] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Attendance summary
  const [attendanceSummary, setAttendanceSummary] = useState(null);
  const [summaryTimeframe, setSummaryTimeframe] = useState(7);
  const [loadingSummary, setLoadingSummary] = useState(false);

  // Manual send form
  const [manualSendForm, setManualSendForm] = useState({
    recipients: '',
    includePDF: true,
    daysBack: 7,  // Default to past week
  });

  // Schedule form
  const [scheduleForm, setScheduleForm] = useState({
    name: '',
    enabled: true,
    period: 'week',
    dayOfWeek: '3',  // Wednesday
    hour: '9',       // 9 AM
    minute: '0',     // 00 minutes
    recipients: '',
    includePDF: true,
    emailSubject: '',
  });

  useEffect(() => {
    loadData();
    loadAttendanceSummary(summaryTimeframe);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([loadSchedules(), loadEmailLogs()]);
    } catch (error) {
      console.error('Error loading data:', error);
      showMessage('error', 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadSchedules = async () => {
    try {
      const manageSchedules = httpsCallable(functions, 'manageReportSchedules');
      const result = await manageSchedules({ action: 'list' });
      setSchedules(result.data.result.schedules || []);
    } catch (error) {
      console.error('Error loading schedules:', error);
      throw error;
    }
  };

  const loadEmailLogs = async () => {
    try {
      const getLogs = httpsCallable(functions, 'getEmailReportLogs');
      const result = await getLogs({ limit: 20 });
      setEmailLogs(result.data.logs || []);
    } catch (error) {
      console.error('Error loading email logs:', error);
      throw error;
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const loadAttendanceSummary = async (days) => {
    setLoadingSummary(true);
    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);
      const data = await fetchReportData(startDate, endDate);
      setAttendanceSummary(data);
    } catch (err) {
      console.error('Error loading attendance summary:', err);
    } finally {
      setLoadingSummary(false);
    }
  };

  const handleSummaryTimeframeChange = (days) => {
    setSummaryTimeframe(days);
    loadAttendanceSummary(days);
  };

  // Fetch report data from Firestore
  const fetchReportData = async (startDate, endDate) => {
    try {
      const checkInsRef = collection(db, 'checkIns');
      const q = query(
        checkInsRef,
        where('checkInTime', '>=', Timestamp.fromDate(startDate)),
        where('checkInTime', '<=', Timestamp.fromDate(endDate))
      );
      const querySnapshot = await getDocs(q);

      const guestMap = new Map();

      // Fetch all participants for additional info
      const participantsSnapshot = await getDocs(collection(db, 'participants'));
      const participantsMap = new Map();
      participantsSnapshot.forEach(doc => {
        const data = doc.data();
        const key = `${data.firstName}_${data.lastName}`;
        participantsMap.set(key, data);
      });

      // Process check-ins and track Sunday vs Tuesday counts
      let sundayCheckIns = 0;
      let tuesdayCheckIns = 0;

      querySnapshot.forEach(doc => {
        const checkIn = doc.data();

        // Skip notes
        if (checkIn.serviceType === 'note') return;

        // Count by service day
        if (checkIn.serviceType === 'tuesday_prayer') {
          tuesdayCheckIns++;
        } else {
          sundayCheckIns++;
        }

        const guestKey = `${checkIn.guestFirstName} ${checkIn.guestLastName}`;
        const participantKey = `${checkIn.guestFirstName}_${checkIn.guestLastName}`;
        const guestInfo = participantsMap.get(participantKey);

        if (!guestMap.has(guestKey)) {
          guestMap.set(guestKey, {
            name: guestKey,
            firstName: checkIn.guestFirstName,
            lastName: checkIn.guestLastName,
            image: guestInfo?.image || null,
            phone: guestInfo?.phone || '',
            email: guestInfo?.email || '',
            emergencyContactName: guestInfo?.emergencyContactName || '',
            emergencyContactPhone: guestInfo?.emergencyContactPhone || '',
            visits: [],
            totalVisits: 0,
            totalDuration: 0,
          });
        }

        const guest = guestMap.get(guestKey);

        // Calculate duration
        let duration = 0;
        if (checkIn.status === 'checked_out' && checkIn.checkOutTime) {
          const checkInMs = checkIn.checkInTime.toMillis();
          const checkOutMs = checkIn.checkOutTime.toMillis();
          duration = Math.round((checkOutMs - checkInMs) / (1000 * 60)); // minutes
        }

        guest.visits.push({
          checkInTime: checkIn.checkInTime,
          checkOutTime: checkIn.checkOutTime,
          serviceType: checkIn.serviceType,
          status: checkIn.status,
          duration: duration,
          pagerNumber: checkIn.pagerNumber,
          notes: checkIn.checkInNotes,
          checkOutNotes: checkIn.checkOutNotes,
          expectedPickupTime: checkIn.expectedPickupTime || '',
          actualPickupTime: checkIn.actualPickupTime || '',
          vidNumber: checkIn.vidNumber || '',
          licPlateNumber: checkIn.licPlateNumber || '',
          vehicleColor: checkIn.vehicleColor || '',
          pickupNotes: checkIn.pickupNotes || '',
        });

        guest.totalVisits++;
        guest.totalDuration += duration;
      });

      // Fetch additional attendance for the date range
      const startDateStr = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
      const endDateStr = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;

      let totalAdditionalSunday = 0;
      let totalAdditionalTuesday = 0;
      try {
        const attendanceRef = collection(db, 'additionalAttendance');
        const attendanceQuery = query(
          attendanceRef,
          where(documentId(), '>=', startDateStr),
          where(documentId(), '<=', endDateStr)
        );
        const attendanceSnapshot = await getDocs(attendanceQuery);
        attendanceSnapshot.forEach(doc => {
          const data = doc.data();
          totalAdditionalSunday += (data.sundayCount || 0);
          totalAdditionalTuesday += (data.tuesdayCount || 0);
        });
      } catch (err) {
        console.error('Error fetching additional attendance for report:', err);
      }

      const guestReports = Array.from(guestMap.values()).sort((a, b) => {
        const nameA = a.name.toLowerCase();
        const nameB = b.name.toLowerCase();
        return nameA.localeCompare(nameB);
      });

      const totalCheckIns = sundayCheckIns + tuesdayCheckIns;

      return {
        timeframe: 'custom',
        startDate,
        endDate,
        totalGuests: guestReports.length,
        totalCheckIns,
        sundayCheckIns,
        tuesdayCheckIns,
        totalAdditionalSunday,
        totalAdditionalTuesday,
        totalSundayAttendance: sundayCheckIns + totalAdditionalSunday,
        totalPrayerMeetingAttendance: tuesdayCheckIns + totalAdditionalTuesday,
        totalAttendance: totalCheckIns + totalAdditionalSunday + totalAdditionalTuesday,
        guests: guestReports,
      };
    } catch (error) {
      console.error('Error fetching report data:', error);
      throw error;
    }
  };

  // Format helpers (from Reports.js)
  const formatFullDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const options = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    };
    return date.toLocaleString('en-US', options);
  };

  const formatDuration = (minutes) => {
    if (!minutes || minutes === 0) return 'N/A';
    const totalMins = Math.round(minutes);
    const hours = Math.floor(totalMins / 60);
    const mins = totalMins % 60;
    if (hours === 0) return `${mins}m`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  const formatServiceType = (serviceType) => {
    if (serviceType === 'tuesday_prayer') return 'TPM';
    return serviceType || 'N/A';
  };

  // Convert "HH:MM" 24h to "h:MM AM/PM"
  const formatTime12h = (timeStr) => {
    if (!timeStr || typeof timeStr !== 'string') return '';
    const parts = timeStr.split(':');
    if (parts.length < 2) return timeStr;
    const h = parseInt(parts[0], 10);
    const mm = parts[1];
    if (isNaN(h)) return timeStr;
    const period = h >= 12 ? 'PM' : 'AM';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${h12}:${mm} ${period}`;
  };

  const formatPickupVehicle = (visit) => {
    const parts = [];
    if (visit.actualPickupTime) parts.push(formatTime12h(visit.actualPickupTime));
    if (visit.vidNumber) parts.push(`VID ${visit.vidNumber}`);
    if (visit.licPlateNumber) parts.push(`Plate ${visit.licPlateNumber}`);
    if (visit.vehicleColor) parts.push(visit.vehicleColor);
    return parts.join(' • ');
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length === 11 && cleaned[0] === '1') {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    } else if (cleaned.length > 10) {
      return `(${cleaned.slice(-10, -7)}) ${cleaned.slice(-7, -4)}-${cleaned.slice(-4)}`;
    }
    return phone;
  };

  const getTimeframeLabel = (timeframe) => {
    switch (timeframe) {
      case 'week': return 'Past Week';
      case 'month': return 'Past Month';
      case 'quarter': return 'Past Quarter (3 Months)';
      case 'semi-annual': return 'Past 6 Months';
      case 'annual': return 'Past Year (12 Months)';
      default: return 'Custom Range';
    }
  };

  // Generate PDF using same approach as Reports.js
  const generateReportPDF = async (reportData) => {
    return new Promise((resolve, reject) => {
      try {
        const isMultipleGuests = reportData.guests.length > 1;
        const reportTitle = isMultipleGuests
          ? 'All Guests Activity Report'
          : `${reportData.guests[0].name} - Guest Activity Report`;

        const htmlContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>${reportTitle}</title>
            <style>
              * {
                box-sizing: border-box;
              }
              body {
                font-family: Arial, sans-serif;
                margin: 0;
                padding: 20px;
                color: #333;
                width: 1050px;
                background: white;
              }
              .header {
                text-align: center;
                margin-bottom: 15px;
                border-bottom: 2px solid #1a5f7a;
                padding-bottom: 10px;
              }
              .header h1 {
                color: #1a5f7a;
                margin: 0 0 5px 0;
                font-size: 22px;
              }
              .header p {
                margin: 0;
                font-size: 12px;
                color: #666;
              }
              .guest-container {
                display: grid;
                grid-template-columns: 260px 130px 1fr;
                grid-template-rows: auto;
                gap: 10px;
                background: #f8fafc;
                padding: 8px 10px;
                border-radius: 6px;
                margin-bottom: 15px;
                width: 100%;
              }
              .guest-profile-section {
                grid-column: 1;
                grid-row: 1;
                display: flex;
                gap: 12px;
                align-items: flex-start;
              }
              .guest-image {
                flex-shrink: 0;
              }
              .guest-image img {
                width: 70px;
                height: 70px;
                border-radius: 50%;
                object-fit: cover;
                border: 3px solid #1a5f7a;
              }
              .guest-avatar {
                width: 70px;
                height: 70px;
                border-radius: 50%;
                background: linear-gradient(135deg, #1a5f7a 0%, #0a3a5e 100%);
                color: white;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 24px;
                font-weight: bold;
                text-transform: uppercase;
                border: 3px solid #1a5f7a;
              }
              .guest-details {
                flex: 1;
                min-width: 0;
              }
              .guest-details h2 {
                color: #1a5f7a;
                margin: 0 0 4px 0;
                font-size: 16px;
                line-height: 1.2;
              }
              .info-row {
                margin: 2px 0;
                font-size: 10px;
                line-height: 1.3;
              }
              .info-label {
                font-weight: bold;
                display: inline-block;
                width: 65px;
              }
              .summary-stats {
                grid-column: 2;
                grid-row: 1;
                display: flex;
                flex-direction: column;
                gap: 6px;
              }
              .stat-card {
                background: white;
                padding: 5px 8px;
                border-radius: 4px;
                border: 1px solid #e5e7eb;
                font-size: 10px;
                line-height: 1.2;
              }
              .stat-label {
                font-size: 9px;
                color: #64748b;
                font-weight: bold;
                text-transform: uppercase;
              }
              .stat-value {
                font-size: 11px;
                font-weight: bold;
                color: #1a5f7a;
              }
              .visit-history-section {
                grid-column: 3;
                grid-row: 1;
                overflow: hidden;
              }
              .visit-history-section h3 {
                margin: 0 0 6px 0;
                font-size: 11px;
                color: #1a5f7a;
                font-weight: 600;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                margin-top: 4px;
                font-size: 9px;
                table-layout: fixed;
              }
              th {
                background: #1a5f7a;
                color: white;
                padding: 5px 3px;
                text-align: left;
                font-size: 8px;
                text-transform: uppercase;
                font-weight: 600;
              }
              th:nth-child(1) { width: 20%; }
              th:nth-child(2) { width: 10%; }
              th:nth-child(3) { width: 20%; }
              th:nth-child(4) { width: 10%; }
              th:nth-child(5) { width: 12%; }
              th:nth-child(6) { width: 28%; }
              .pickup-notes-line {
                margin-top: 2px;
                font-size: 8px;
                color: #6b7280;
                font-style: italic;
                white-space: normal;
              }
              td {
                padding: 3px;
                border-bottom: 1px solid #e5e7eb;
                line-height: 1.3;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              }
              .status-completed {
                background: #10b981;
                color: white;
                padding: 2px 5px;
                border-radius: 3px;
                font-size: 8px;
                font-weight: bold;
                white-space: nowrap;
              }
              .status-active {
                background: #f59e0b;
                color: white;
                padding: 2px 5px;
                border-radius: 3px;
                font-size: 8px;
                font-weight: bold;
                white-space: nowrap;
              }
              .service-tag {
                background: #1a5f7a;
                color: white;
                padding: 2px 5px;
                border-radius: 3px;
                font-size: 8px;
                font-weight: bold;
                white-space: nowrap;
              }
              .footer {
                margin-top: 20px;
                text-align: center;
                font-size: 10px;
                color: #666;
                border-top: 1px solid #e5e7eb;
                padding-top: 10px;
              }
              @media print {
                body {
                  margin: 15px;
                }
                .no-print { display: none; }
                @page {
                  margin: 0.5in;
                }
              }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>${isMultipleGuests ? 'All Guests Activity Report' : 'Guest Activity Report'}</h1>
              <p>AAR Guest Management System</p>
              <p style="margin-top: 5px;">Timeframe: ${getTimeframeLabel(reportData.timeframe)} | ${reportData.startDate.toLocaleDateString()} - ${reportData.endDate.toLocaleDateString()}</p>
              ${isMultipleGuests ? `
                <p style="margin-top: 5px;">Total Guests: <strong>${reportData.guests.length}</strong> | Total Check-Ins: <strong>${reportData.totalCheckIns}</strong> | Total Attendance: <strong>${reportData.totalAttendance}</strong></p>
                <p style="margin-top: 3px; font-size: 11px;">
                  Total Sunday Attendance: <strong>${reportData.totalSundayAttendance}</strong>
                  (${reportData.sundayCheckIns} checked in + ${reportData.totalAdditionalSunday} additional)
                  &nbsp;&bull;&nbsp;
                  Total Prayer Meeting Attendance: <strong>${reportData.totalPrayerMeetingAttendance}</strong>
                  (${reportData.tuesdayCheckIns} checked in + ${reportData.totalAdditionalTuesday} additional)
                </p>
              ` : ''}
            </div>

            ${reportData.guests.map((guest, index) => `
              <div style="page-break-inside: avoid; ${(index > 0 && index % 5 === 0) ? 'page-break-before: always;' : ''}">
                <div class="guest-container">
                  <div class="guest-profile-section">
                    <div class="guest-image">
                      ${guest.image
                        ? `<img src="${guest.image}" alt="${guest.name}" />`
                        : `<img src="${placeholderAvatar}" alt="${guest.name}" style="width: 100%; height: 100%; object-fit: cover; border-radius: 50%;" />`
                      }
                    </div>
                    <div class="guest-details">
                      <h2>${guest.name}</h2>
                      ${guest.phone ? `<div class="info-row"><span class="info-label">Phone:</span> ${formatPhoneNumber(guest.phone)}</div>` : ''}
                      ${guest.email ? `<div class="info-row"><span class="info-label">Email:</span> ${guest.email}</div>` : ''}
                      ${guest.emergencyContactName ? `<div class="info-row"><span class="info-label">Emergency:</span> ${guest.emergencyContactName}${guest.emergencyContactPhone ? ` - ${formatPhoneNumber(guest.emergencyContactPhone)}` : ''}</div>` : ''}
                    </div>
                  </div>

                  <div class="summary-stats">
                    <div class="stat-card">
                      <span class="stat-label">Total Visits:</span> <span class="stat-value">${guest.totalVisits}</span>
                    </div>
                    <div class="stat-card">
                      <span class="stat-label">Total Duration:</span> <span class="stat-value">${formatDuration(guest.totalDuration)}</span>
                    </div>
                    ${guest.totalVisits > 0 ? `
                    <div class="stat-card">
                      <span class="stat-label">Avg Duration:</span> <span class="stat-value">${formatDuration(guest.totalDuration / guest.totalVisits)}</span>
                    </div>
                    ` : ''}
                  </div>

                  <div class="visit-history-section">
                    <h3>Visit History</h3>
                    <table>
                  <thead>
                    <tr>
                      <th>Check-In</th>
                      <th>Service</th>
                      <th>Check-Out</th>
                      <th>Duration</th>
                      <th>Status</th>
                      <th>Pickup / Vehicle</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${guest.visits.map(visit => `
                      <tr>
                        <td>${formatFullDateTime(visit.checkInTime)}</td>
                        <td><span class="service-tag">${formatServiceType(visit.serviceType)}</span></td>
                        <td>${visit.checkOutTime ? formatFullDateTime(visit.checkOutTime) : '—'}</td>
                        <td>${visit.duration > 0 ? formatDuration(visit.duration) : '—'}</td>
                        <td><span class="status-${visit.status === 'checked_out' ? 'completed' : 'active'}">${visit.status === 'checked_out' ? 'Completed' : 'Active'}</span></td>
                        <td style="white-space: normal; word-break: break-word; line-height: 1.3;">
                          <div>${formatPickupVehicle(visit) || '—'}</div>
                          ${visit.pickupNotes ? `<div class="pickup-notes-line">${visit.pickupNotes}</div>` : ''}
                        </td>
                      </tr>
                    `).join('')}
                  </tbody>
                    </table>
                  </div>
                </div>
              </div>
            `).join('')}

            <div class="footer">
              <p>Generated on ${new Date().toLocaleString()}</p>
              <p>AAR Guest Management System - Guest Activity Report</p>
            </div>
          </body>
          </html>
        `;

        // Create hidden iframe to print and capture PDF
        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.top = '-10000px';
        iframe.style.left = '-10000px';
        document.body.appendChild(iframe);

        const iframeDoc = iframe.contentWindow.document;
        iframeDoc.open();
        iframeDoc.write(htmlContent);
        iframeDoc.close();

        // Wait for images to load
        setTimeout(async () => {
          try {
            // Use html2canvas and jsPDF to generate PDF
            const html2canvas = (await import('html2canvas')).default;
            const jsPDF = (await import('jspdf')).default;

            const canvas = await html2canvas(iframeDoc.body, {
              scale: 1.2, // Reduced from 1.5 for smaller file size
              useCORS: true,
              allowTaint: false,
              logging: false,
            });

            // Use JPEG with higher compression for smaller file size
            const imgData = canvas.toDataURL('image/jpeg', 0.7);
            const pdf = new jsPDF({
              orientation: 'landscape',
              unit: 'mm',
              format: 'letter',
              compress: true, // Enable PDF compression
            });

            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const imgWidth = pageWidth;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            let heightLeft = imgHeight;
            let position = 0;

            pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;

            while (heightLeft > 0) {
              position = heightLeft - imgHeight;
              pdf.addPage();
              pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
              heightLeft -= pageHeight;
            }

            const pdfBase64 = pdf.output('datauristring').split(',')[1];

            // Cleanup
            document.body.removeChild(iframe);

            console.log(`PDF generated: ${pdfBase64.length} chars base64`);
            resolve(pdfBase64);
          } catch (error) {
            document.body.removeChild(iframe);
            reject(error);
          }
        }, 2000); // Increased wait time for images to load
      } catch (error) {
        console.error('Error generating PDF:', error);
        reject(error);
      }
    });
  };

  const handleManualSend = async (e) => {
    e.preventDefault();
    setSending(true);

    try {
      const recipientsList = manualSendForm.recipients
        .split(',')
        .map(email => email.trim())
        .filter(email => email);

      if (recipientsList.length === 0) {
        showMessage('error', 'Please enter at least one email address');
        setSending(false);
        return;
      }

      // Calculate date range
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - manualSendForm.daysBack);

      // Fetch report data from Firestore
      const reportData = await fetchReportData(startDate, endDate);

      // Generate PDF if requested
      let pdfBase64 = null;
      if (manualSendForm.includePDF) {
        try {
          showMessage('info', 'Generating PDF... This may take a moment.');
          pdfBase64 = await generateReportPDF(reportData);
          console.log('PDF generated successfully');
        } catch (pdfError) {
          console.error('PDF generation error:', pdfError);
          showMessage('warning', 'Could not generate PDF. Sending email without PDF attachment.');
          // Continue without PDF
        }
      }

      // Send email with or without PDF
      const sendReport = httpsCallable(functions, 'sendGuestActivityReportManual');
      const result = await sendReport({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        recipients: recipientsList,
        pdfBase64,
        reportSummary: {
          totalGuests: reportData.totalGuests,
          totalCheckIns: reportData.totalCheckIns,
          totalAttendance: reportData.totalAttendance,
          totalSundayAttendance: reportData.totalSundayAttendance,
          totalPrayerMeetingAttendance: reportData.totalPrayerMeetingAttendance,
          sundayCheckIns: reportData.sundayCheckIns,
          tuesdayCheckIns: reportData.tuesdayCheckIns,
          totalAdditionalSunday: reportData.totalAdditionalSunday,
          totalAdditionalTuesday: reportData.totalAdditionalTuesday,
          timeframe: 'custom',
        },
      });

      if (result.data.success) {
        showMessage(
          'success',
          `Report sent successfully to ${result.data.recipients} recipient(s). ` +
          `${result.data.totalGuests} guests, ${result.data.totalCheckIns} check-ins.`
        );
        setManualSendForm({ ...manualSendForm, recipients: '' });
        await loadEmailLogs();
      } else {
        showMessage('error', `Failed to send report: ${result.data.status}`);
      }
    } catch (error) {
      console.error('Error sending manual report:', error);
      showMessage('error', error.message || 'Failed to send report');
    } finally {
      setSending(false);
    }
  };

  const handleSaveSchedule = async (e) => {
    e.preventDefault();

    try {
      const recipientsList = scheduleForm.recipients
        .split(',')
        .map(email => email.trim())
        .filter(email => email);

      if (recipientsList.length === 0) {
        showMessage('error', 'Please enter at least one email address');
        return;
      }

      // Convert day/time to cron format: minute hour day month dayOfWeek
      const cronSchedule = `${scheduleForm.minute} ${scheduleForm.hour} * * ${scheduleForm.dayOfWeek}`;

      const scheduleData = {
        name: scheduleForm.name,
        enabled: scheduleForm.enabled,
        period: scheduleForm.period,
        cronSchedule: cronSchedule,
        recipients: recipientsList,
        includePDF: scheduleForm.includePDF,
        emailSubject: scheduleForm.emailSubject || null,
      };

      const manageSchedules = httpsCallable(functions, 'manageReportSchedules');

      if (editingSchedule) {
        await manageSchedules({
          action: 'update',
          scheduleId: editingSchedule.id,
          schedule: scheduleData,
        });
        showMessage('success', 'Schedule updated! Redeploy Cloud Functions to activate changes.');
      } else {
        await manageSchedules({
          action: 'add',
          schedule: scheduleData,
        });
        showMessage('success', 'Schedule created! Redeploy Cloud Functions to activate schedule.');
      }

      setShowScheduleModal(false);
      setEditingSchedule(null);
      resetScheduleForm();
      await loadSchedules();
    } catch (error) {
      console.error('Error saving schedule:', error);
      showMessage('error', error.message || 'Failed to save schedule');
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm('Are you sure you want to delete this schedule?')) {
      return;
    }

    try {
      const manageSchedules = httpsCallable(functions, 'manageReportSchedules');
      await manageSchedules({
        action: 'delete',
        scheduleId,
      });

      showMessage('success', 'Schedule deleted! Redeploy Cloud Functions to remove schedule.');
      await loadSchedules();
    } catch (error) {
      console.error('Error deleting schedule:', error);
      showMessage('error', error.message || 'Failed to delete schedule');
    }
  };

  const handleEditSchedule = (schedule) => {
    setEditingSchedule(schedule);

    // Parse cron schedule to day/time
    const parts = schedule.cronSchedule.split(' ');
    const minute = parts[0] || '0';
    const hour = parts[1] || '9';
    const dayOfWeek = parts[4] || '3';

    setScheduleForm({
      name: schedule.name,
      enabled: schedule.enabled,
      period: schedule.period,
      dayOfWeek: dayOfWeek,
      hour: hour,
      minute: minute,
      recipients: schedule.recipients.join(', '),
      includePDF: schedule.includePDF,
      emailSubject: schedule.emailSubject || '',
    });
    setShowScheduleModal(true);
  };

  const resetScheduleForm = () => {
    setScheduleForm({
      name: '',
      enabled: true,
      period: 'week',
      dayOfWeek: '3',  // Wednesday
      hour: '9',       // 9 AM
      minute: '0',     // 00 minutes
      recipients: '',
      includePDF: true,
      emailSubject: '',
    });
  };

  const parseCronDescription = (cronSchedule) => {
    try {
      const parts = cronSchedule.split(' ');
      if (parts.length !== 5) return cronSchedule;

      const [minute, hour, , , dayOfWeek] = parts;
      const daysMap = { '0': 'Sun', '1': 'Mon', '2': 'Tue', '3': 'Wed', '4': 'Thu', '5': 'Fri', '6': 'Sat' };

      const hourNum = parseInt(hour);
      const minuteNum = parseInt(minute);
      const ampm = hourNum >= 12 ? 'PM' : 'AM';
      const hour12 = hourNum === 0 ? 12 : hourNum > 12 ? hourNum - 12 : hourNum;
      const timeStr = `${hour12}:${minuteNum.toString().padStart(2, '0')} ${ampm}`;

      if (dayOfWeek !== '*') {
        const days = dayOfWeek.split(',').map(d => daysMap[d] || d);
        return `${days.join(', ')} at ${timeStr}`;
      }

      return `Every day at ${timeStr}`;
    } catch (error) {
      return cronSchedule;
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';

    try {
      // Handle Firestore Timestamp object
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        const date = timestamp.toDate();
        return date.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      }

      // Handle serialized Firestore Timestamp from Cloud Functions (_seconds, _nanoseconds)
      if (timestamp._seconds !== undefined) {
        const date = new Date(timestamp._seconds * 1000);
        return date.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      }

      // Handle seconds/nanoseconds object (seconds, nanoseconds)
      if (timestamp.seconds !== undefined) {
        const date = new Date(timestamp.seconds * 1000);
        return date.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      }

      // Handle ISO string or regular Date
      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) {
        return date.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      }

      console.error('Unable to format timestamp:', timestamp);
      return 'Invalid Date';
    } catch (error) {
      console.error('Error formatting timestamp:', error, timestamp);
      return 'Invalid Date';
    }
  };

  // Render the recipient email address(es) stored on an email log entry
  const formatRecipients = (recipients) => {
    if (!recipients) return '—';
    if (Array.isArray(recipients)) {
      const emails = recipients
        .map((r) => (typeof r === 'string' ? r : r?.email))
        .filter(Boolean);
      return emails.length > 0 ? emails.join(', ') : '—';
    }
    return typeof recipients === 'string' ? recipients : '—';
  };

  if (loading) {
    return (
      <div className="loading"><div className="spinner"></div><p>Loading...</p></div>
    );
  }

  return (
    <div className="guest-activity-reports">
      {message.text && (
        <div className={`message message-${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Attendance Summary Section */}
      <div className="report-section">
        <div className="section-header">
          <h2>Attendance Summary</h2>
          <select
            value={summaryTimeframe}
            onChange={(e) => handleSummaryTimeframeChange(parseInt(e.target.value))}
            className="timeframe-select"
            disabled={loadingSummary}
          >
            <option value={7}>Past Week</option>
            <option value={14}>Past 2 Weeks</option>
            <option value={30}>Past Month</option>
            <option value={90}>Past Quarter</option>
            <option value={180}>Past 6 Months</option>
            <option value={365}>Past Year</option>
          </select>
        </div>

        {loadingSummary ? (
          <p style={{ color: '#666', padding: '16px 0' }}>Loading attendance data...</p>
        ) : attendanceSummary ? (
          <div className="attendance-summary-grid">
            <div className="attendance-summary-card total">
              <div className="attendance-summary-number">{attendanceSummary.totalAttendance}</div>
              <div className="attendance-summary-label">Total Attendance</div>
              <div className="attendance-summary-detail">
                {attendanceSummary.totalCheckIns} checked in + {attendanceSummary.totalAdditionalSunday + attendanceSummary.totalAdditionalTuesday} additional
              </div>
            </div>
            <div className="attendance-summary-card sunday">
              <div className="attendance-summary-number">{attendanceSummary.totalSundayAttendance}</div>
              <div className="attendance-summary-label">Total Sunday Attendance</div>
              <div className="attendance-summary-detail">
                {attendanceSummary.sundayCheckIns} checked in + {attendanceSummary.totalAdditionalSunday} additional
              </div>
            </div>
            <div className="attendance-summary-card tuesday">
              <div className="attendance-summary-number">{attendanceSummary.totalPrayerMeetingAttendance}</div>
              <div className="attendance-summary-label">Total Prayer Meeting Attendance</div>
              <div className="attendance-summary-detail">
                {attendanceSummary.tuesdayCheckIns} checked in + {attendanceSummary.totalAdditionalTuesday} additional
              </div>
            </div>
            <div className="attendance-summary-card guests">
              <div className="attendance-summary-number">{attendanceSummary.totalGuests}</div>
              <div className="attendance-summary-label">Unique Guests</div>
              <div className="attendance-summary-detail">
                {attendanceSummary.totalCheckIns} total check-ins
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* Manual Send Section */}
      <div className="report-section">
        <h2>Manual Email Test</h2>
        <form onSubmit={handleManualSend} className="manual-send-form">
          <div className="guest-activity-form-row">
            <div className="form-group">
              <label>Report Period</label>
              <select
                value={manualSendForm.daysBack}
                onChange={(e) => setManualSendForm({ ...manualSendForm, daysBack: parseInt(e.target.value) })}
                disabled={sending}
              >
                <option value={7}>Past Week (7 days)</option>
                <option value={14}>Past 2 Weeks</option>
                <option value={30}>Past Month</option>
                <option value={90}>Past 3 Months</option>
              </select>
            </div>

            <div className="form-group">
              <label>Recipient Email(s) (comma-separated)</label>
              <input
                type="text"
                placeholder="email1@example.com, email2@example.com"
                value={manualSendForm.recipients}
                onChange={(e) => setManualSendForm({ ...manualSendForm, recipients: e.target.value })}
                required
                disabled={sending}
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={sending || ro.disabled} title={ro.title}>
              {sending ? 'Sending...' : 'Send Test Email'}
            </button>
          </div>
        </form>
      </div>

      {/* Schedules Section */}
      <div className="report-section">
        <div className="section-header">
          <h2>Email Schedules</h2>
          <button
            className="btn btn-primary"
            onClick={() => {
              setEditingSchedule(null);
              resetScheduleForm();
              setShowScheduleModal(true);
            }}
            disabled={ro.disabled}
            title={ro.title}
          >
            + Add Schedule
          </button>
        </div>

        <table className="schedules-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Schedule</th>
              <th>Recipients</th>
              <th>Last Sent</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {schedules.length === 0 ? (
              <tr>
                <td colSpan="6" style={{textAlign: 'center', color: '#999'}}>
                  No schedules configured. Create one to get started.
                </td>
              </tr>
            ) : (
              schedules.map((schedule) => (
                <tr key={schedule.id} className={!schedule.enabled ? 'disabled' : ''}>
                  <td>{schedule.name}</td>
                  <td>{parseCronDescription(schedule.cronSchedule)}</td>
                  <td>{schedule.recipients.length} email(s)</td>
                  <td>{formatTimestamp(schedule.lastSent)}</td>
                  <td>
                    <span className={`status-badge ${schedule.enabled ? 'enabled' : 'disabled'}`}>
                      {schedule.enabled ? 'Active' : 'Disabled'}
                    </span>
                  </td>
                  <td>
                    <div className="schedule-actions">
                      <button className="btn btn-outline btn-sm" onClick={() => handleEditSchedule(schedule)} disabled={ro.disabled} title={ro.title}>
                        Edit
                      </button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDeleteSchedule(schedule.id)} disabled={ro.disabled} title={ro.title}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Email Logs Section */}
      <div className="report-section">
        <h2>Recent Email History</h2>
        {emailLogs.length === 0 ? (
          <p className="empty-state">No emails sent yet.</p>
        ) : (
          <div className="email-logs-table">
            <table>
              <thead>
                <tr>
                  <th>Date & Time</th>
                  <th>Schedule</th>
                  <th>Recipients</th>
                  <th>Email Address(es)</th>
                  <th>Guests</th>
                  <th>Check-Ins</th>
                  <th>PDF</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {emailLogs.map((log) => (
                  <tr key={log.id}>
                    <td>{formatTimestamp(log.sentAt)}</td>
                    <td>{log.scheduleId === 'manual' ? 'Manual Send' : log.scheduleId}</td>
                    <td>{log.recipients?.length || 0}</td>
                    <td className="recipient-emails" title={formatRecipients(log.recipients)}>
                      {formatRecipients(log.recipients)}
                    </td>
                    <td>{log.totalGuests || 0}</td>
                    <td>{log.totalCheckIns || 0}</td>
                    <td>{log.pdfAttached ? 'Yes' : 'No'}</td>
                    <td>
                      <span className={`status-badge ${log.status === 'sent' ? 'success' : 'error'}`}>
                        {log.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="modal-overlay" onClick={() => setShowScheduleModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingSchedule ? 'Edit Schedule' : 'Create Schedule'}</h2>
              <button className="modal-close" onClick={() => setShowScheduleModal(false)}>×</button>
            </div>
            <form onSubmit={handleSaveSchedule} className="schedule-form">
              <div className="form-group">
                <label>Schedule Name *</label>
                <input
                  type="text"
                  placeholder="e.g., Weekly Executive Report"
                  value={scheduleForm.name}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, name: e.target.value })}
                  required
                />
              </div>

              <div className="guest-activity-form-row">
                <div className="form-group">
                  <label>Day of Week *</label>
                  <select
                    value={scheduleForm.dayOfWeek}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, dayOfWeek: e.target.value })}
                    required
                  >
                    <option value="0">Sunday</option>
                    <option value="1">Monday</option>
                    <option value="2">Tuesday</option>
                    <option value="3">Wednesday</option>
                    <option value="4">Thursday</option>
                    <option value="5">Friday</option>
                    <option value="6">Saturday</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Time *</label>
                  <div style={{display: 'flex', gap: '10px'}}>
                    <select
                      value={scheduleForm.hour}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, hour: e.target.value })}
                      required
                      style={{flex: 1}}
                    >
                      {Array.from({length: 24}, (_, i) => (
                        <option key={i} value={i}>
                          {i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i-12} PM`}
                        </option>
                      ))}
                    </select>
                    <select
                      value={scheduleForm.minute}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, minute: e.target.value })}
                      required
                      style={{flex: 1}}
                    >
                      <option value="0">:00</option>
                      <option value="15">:15</option>
                      <option value="30">:30</option>
                      <option value="45">:45</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Period</label>
                  <select
                    value={scheduleForm.period}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, period: e.target.value })}
                  >
                    <option value="day">Daily</option>
                    <option value="week">Weekly</option>
                    <option value="month">Monthly</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Recipients (comma-separated) *</label>
                <textarea
                  placeholder="email1@example.com, email2@example.com"
                  value={scheduleForm.recipients}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, recipients: e.target.value })}
                  required
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label>Email Subject (optional)</label>
                <input
                  type="text"
                  placeholder="Leave blank for default"
                  value={scheduleForm.emailSubject}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, emailSubject: e.target.value })}
                />
              </div>

              <div className="guest-activity-form-row">
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={scheduleForm.includePDF}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, includePDF: e.target.checked })}
                    />
                    Include PDF Attachment
                  </label>
                </div>

                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={scheduleForm.enabled}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, enabled: e.target.checked })}
                    />
                    Enabled
                  </label>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={() => setShowScheduleModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={ro.disabled} title={ro.title}>
                  {editingSchedule ? 'Update Schedule' : 'Create Schedule'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuestActivityReports;
